import { z } from 'zod';
import { fromKstDateTimeInput, type UtcIsoString } from '@platform/datetime';
import {
  FAQ_CATEGORIES,
  FAQ_SUB_CATEGORY_MAX_LENGTH,
  isFaqSubCategory,
} from '../constants/faq.ts';
import {
  ARCHIVE_CATEGORIES,
  isArchiveSubCategory,
} from '../constants/archive.ts';

/**
 * 게시글 작성/수정 입력 검증.
 * 백엔드 Create/UpdatePostDto 와 규칙을 동기화한다.
 *
 * 본문(body)은 Tiptap JSON 이라 zod 로 깊게 검증하지 않고,
 * "비어있지 않음"만 컴포넌트단(isEmptyDoc)에서 막는다.
 * (구조 검증을 zod 로 하면 Tiptap 스키마와 이중 관리가 되어 깨지기 쉬움)
 */

export const titleField = z
  .string()
  .min(1, '제목을 입력해주세요.')
  .max(200, '제목은 200자 이하여야 합니다.');

export const excerptField = z
  .string()
  .max(300, '요약은 300자 이하여야 합니다.')
  .optional()
  .or(z.literal(''));

/** 공통 폼 필드(본문 제외 — 본문은 별도 상태) */
export const postCommonSchema = z.object({
  title: titleField,
  excerpt: excerptField,
  isPinned: z.boolean().optional(),
});

export type PostCommonValues = z.infer<typeof postCommonSchema>;

/* ── 게시판 타입별 meta 스키마 ──────────────────────────────────── */

/** 공지: 카테고리 */
export const noticeMetaSchema = z.object({
  category: z.enum(['GENERAL', 'STANDARD', 'WAGE', 'EMERGENCY']),
});
export type NoticeMetaValues = z.infer<typeof noticeMetaSchema>;

/** FAQ: 분류 (BE 는 meta.faqCategory 로 받음) */
export const faqMetaSchema = z
  .object({
    faqCategory: z.enum(FAQ_CATEGORIES, {
      message: '대분류를 선택해주세요.',
    }),
    faqSubCategory: z
      .string()
      .min(1, '중분류를 선택해주세요.')
      .max(FAQ_SUB_CATEGORY_MAX_LENGTH, '중분류 값이 너무 깁니다.'),
  })
  .refine(
    ({ faqCategory, faqSubCategory }) =>
      isFaqSubCategory(faqCategory, faqSubCategory),
    {
      path: ['faqSubCategory'],
      message: '대분류에 맞는 중분류를 선택해주세요.',
    },
  );
export type FaqMetaValues = z.infer<typeof faqMetaSchema>;

/** 자료실: 분류 + 하위 분류(분류에 하위 분류가 있을 때만 필수). BE 는 meta.archiveCategory 로 받음 */
export const archiveMetaSchema = z
  .object({
    archiveCategory: z.enum(ARCHIVE_CATEGORIES, {
      message: '분류를 선택해주세요.',
    }),
    archiveSubCategory: z.string().optional().or(z.literal('')),
  })
  .refine(
    ({ archiveCategory, archiveSubCategory }) =>
      isArchiveSubCategory(archiveCategory, archiveSubCategory),
    {
      path: ['archiveSubCategory'],
      message: '분류에 맞는 하위 분류를 선택해주세요.',
    },
  );
export type ArchiveMetaValues = z.infer<typeof archiveMetaSchema>;

/** 업데이트: 제품 + 버전 */
export const updateMetaSchema = z.object({
  productSlug: z.string().min(1, '제품을 선택해주세요.'),
  version: z
    .string()
    .max(40, '버전 표기가 너무 깁니다.')
    .optional()
    .or(z.literal('')),
});
export type UpdateMetaValues = z.infer<typeof updateMetaSchema>;

/** `datetime-local` 값(KST) → UTC ISO. 오프셋 없는 값을 그대로 보내면 서버가 거부한다. */
const kstDateTimeInput = (requiredMessage: string) =>
  z
    .string()
    .min(1, requiredMessage)
    .transform((value, ctx): UtcIsoString => {
      const iso = fromKstDateTimeInput(value);
      if (!iso) {
        ctx.addIssue({ code: 'custom', message: '날짜·시각 형식이 올바르지 않습니다.' });
        return z.NEVER;
      }
      return iso;
    });

/** 이벤트: 기간 + 신청 링크 */
export const eventMetaSchema = z
  .object({
    startsAt: kstDateTimeInput('시작일을 입력해주세요.'),
    endsAt: kstDateTimeInput('종료일을 입력해주세요.'),
    applyUrl: z
      .string()
      .url('올바른 URL 형식이 아닙니다.')
      .optional()
      .or(z.literal('')),
  })
  .refine((v) => v.startsAt <= v.endsAt, {
    path: ['endsAt'],
    message: '종료일은 시작일 이후여야 합니다.',
  });
export type EventMetaValues = z.infer<typeof eventMetaSchema>;
